import React, { useEffect, useRef, useState } from 'react';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { CalendarDays, ChevronDown, X } from 'lucide-react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import './DateRangePicker.css';

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};

const addDays = (d, n) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() + n);
  return x;
};

const addMonths = (d, n) => {
  const x = startOfDay(d);
  x.setMonth(x.getMonth() + n);
  return x;
};

export const PRESETS = ['today', 'yesterday', '7d', '14d', '30d', '90d', 'lastMonth', '6m', '12m', 'qtd', 'ytd', 'custom'];

export const computeRangeForPreset = (preset, now = new Date()) => {
  const today = startOfDay(now);
  switch (preset) {
    case 'today':
      return { startDate: today, endDate: today };
    case 'yesterday': {
      const y = addDays(today, -1);
      return { startDate: y, endDate: y };
    }
    case '7d':
      return { startDate: addDays(today, -6), endDate: today };
    case '14d':
      return { startDate: addDays(today, -13), endDate: today };
    case '30d':
      return { startDate: addDays(today, -29), endDate: today };
    case '90d':
      return { startDate: addDays(today, -89), endDate: today };
    case 'lastMonth': {
      const startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const endDate = new Date(today.getFullYear(), today.getMonth(), 0);
      return { startDate: startOfDay(startDate), endDate: startOfDay(endDate) };
    }
    case '6m':
      return { startDate: addMonths(today, -6), endDate: today };
    case '12m':
      return { startDate: addMonths(today, -12), endDate: today };
    case 'qtd': {
      const quarterStartMonth = Math.floor(today.getMonth() / 3) * 3;
      return { startDate: startOfDay(new Date(today.getFullYear(), quarterStartMonth, 1)), endDate: today };
    }
    case 'ytd':
      return { startDate: startOfDay(new Date(today.getFullYear(), 0, 1)), endDate: today };
    default:
      return { startDate: today, endDate: today };
  }
};

const formatShort = (date) =>
  date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

const PRESET_LABELS = {
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': '7D',
  '14d': '14D',
  '30d': '30D',
  '90d': '90D',
  lastMonth: 'Last Month',
  '6m': '6M',
  '12m': '12M',
  qtd: 'QTD',
  ytd: 'YTD',
};

// mode="dialog" — compact trigger showing the resolved date range; clicking
// it opens a centered modal with quick-select presets and a two-month range
// calendar. Sidesteps having to fit the whole picker inside a narrow
// container (e.g. the Quality KPI sidebar, QA-7152) since the modal isn't
// constrained by the trigger's width. Every other caller keeps the default
// "inline" mode and is unaffected.
function DateRangePicker({ value, preset = '7d', onChange, maxDate, enabledPresets, orientation = 'horizontal', mode = 'inline', disabled = false }) {
  const visiblePresetLabels = enabledPresets
    ? Object.fromEntries(Object.entries(PRESET_LABELS).filter(([k]) => enabledPresets.includes(k)))
    : PRESET_LABELS;
  const dialogPresetLabels = Object.fromEntries(Object.entries(visiblePresetLabels).filter(([k]) => k !== 'custom'));
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(value?.startDate || null);
  const [draftEnd, setDraftEnd] = useState(value?.endDate || null);
  const [draftPresetKey, setDraftPresetKey] = useState(preset !== 'custom' ? preset : null);
  const [calendarNavKey, setCalendarNavKey] = useState(0);
  const wrapperRef = useRef(null);
  const isDialog = mode === 'dialog';

  useEffect(() => {
    if (!open || isDialog) return undefined;
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open, isDialog]);

  useEffect(() => {
    if (!open || !isDialog) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, isDialog]);

  useEffect(() => {
    setDraftStart(value?.startDate || null);
    setDraftEnd(value?.endDate || null);
  }, [value?.startDate, value?.endDate]);

  const handlePresetChange = (_e, next) => {
    if (!next) return;
    if (next === 'custom') {
      setDraftStart(null);
      setDraftEnd(null);
      setOpen(true);
      return;
    }
    const range = computeRangeForPreset(next);
    onChange({ ...range, preset: next });
  };

  const handleCustomClick = () => {
    if (preset === 'custom') {
      setOpen((o) => !o);
    }
  };

  const handleCalendarChange = (dates) => {
    const [start, end] = dates;
    setDraftStart(start);
    setDraftEnd(end);
    if (start && end) {
      onChange({ startDate: startOfDay(start), endDate: startOfDay(end), preset: 'custom' });
      setOpen(false);
    }
  };

  // Dialog mode doesn't apply on click — it stages a draft (highlighted on
  // the calendar) so the user can still adjust it, then confirms with OK.
  const openDialog = () => {
    if (disabled) return;
    setDraftStart(value?.startDate || null);
    setDraftEnd(value?.endDate || null);
    setDraftPresetKey(preset !== 'custom' ? preset : null);
    setOpen(true);
  };

  const handleDialogPresetClick = (key) => {
    const range = computeRangeForPreset(key);
    setDraftStart(range.startDate);
    setDraftEnd(range.endDate);
    setDraftPresetKey(key);
    setCalendarNavKey((k) => k + 1);
  };

  const handleDialogCalendarChange = (dates) => {
    const [start, end] = dates;
    setDraftStart(start);
    setDraftEnd(end);
    setDraftPresetKey(null);
  };

  const handleDialogOk = () => {
    if (!draftStart || !draftEnd) return;
    onChange({
      startDate: startOfDay(draftStart),
      endDate: startOfDay(draftEnd),
      preset: draftPresetKey || 'custom',
    });
    setOpen(false);
  };

  const handleDialogCancel = () => {
    setOpen(false);
  };

  const customLabel =
    preset === 'custom' && value?.startDate && value?.endDate
      ? `${formatShort(value.startDate)} – ${formatShort(value.endDate)}`
      : 'Custom';

  const triggerLabel =
    value?.startDate && value?.endDate
      ? `${formatShort(value.startDate)} – ${formatShort(value.endDate)}`
      : 'Select dates';

  const effectiveMaxDate = maxDate || new Date();

  if (isDialog) {
    return (
      <div className="date-range-picker date-range-picker--dialog" ref={wrapperRef}>
        <button
          type="button"
          className="date-range-dialog-trigger"
          onClick={openDialog}
          disabled={disabled}
        >
          <CalendarDays size={16} className="date-range-leading-icon" />
          <span className="date-range-dialog-trigger-label">{triggerLabel}</span>
          <ChevronDown size={14} />
        </button>

        {open && !disabled && (
          <div className="date-range-dialog-overlay" onMouseDown={handleDialogCancel}>
            <div
              className="date-range-dialog"
              role="dialog"
              aria-modal="true"
              aria-label="Select date range"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <div className="date-range-dialog-header">
                <span className="date-range-dialog-title">Date Range</span>
                <button
                  type="button"
                  className="date-range-dialog-close"
                  onClick={handleDialogCancel}
                  aria-label="Close"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="date-range-dialog-body">
                {Object.keys(dialogPresetLabels).length > 0 && (
                  <div className="date-range-dialog-presets-col">
                    {Object.entries(dialogPresetLabels).map(([key, label]) => (
                      <button
                        key={key}
                        type="button"
                        className={`date-range-dialog-preset${draftPresetKey === key ? ' active' : ''}`}
                        onClick={() => handleDialogPresetClick(key)}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                )}

                <DatePicker
                  key={calendarNavKey}
                  selected={draftStart}
                  onChange={handleDialogCalendarChange}
                  startDate={draftStart}
                  endDate={draftEnd}
                  openToDate={draftEnd || effectiveMaxDate}
                  maxDate={effectiveMaxDate}
                  selectsRange
                  monthsShown={2}
                  inline
                />
              </div>

              <div className="date-range-dialog-footer">
                <button type="button" className="date-range-dialog-cancel" onClick={handleDialogCancel}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="date-range-dialog-ok"
                  onClick={handleDialogOk}
                  disabled={!draftStart || !draftEnd}
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`date-range-picker date-range-picker--${orientation}`} ref={wrapperRef}>
      <div className="date-range-shell">
        <CalendarDays size={16} className="date-range-leading-icon" />
        <ToggleButtonGroup
          className="date-range-toggle-group"
          value={preset}
          exclusive
          disabled={disabled}
          onChange={handlePresetChange}
          size="small"
          orientation={orientation}
        >
          {Object.entries(visiblePresetLabels).map(([key, label]) => (
            <ToggleButton key={key} value={key} className="date-range-toggle">
              {label}
            </ToggleButton>
          ))}
          <ToggleButton
            value="custom"
            className="date-range-toggle date-range-custom"
            onClick={handleCustomClick}
          >
            {customLabel}
            <ChevronDown size={14} style={{ marginLeft: 4 }} />
          </ToggleButton>
        </ToggleButtonGroup>
      </div>

      {open && (
        <div className="date-range-popover">
          <DatePicker
            selected={draftStart}
            onChange={handleCalendarChange}
            startDate={draftStart}
            endDate={draftEnd}
            openToDate={draftStart || draftEnd || effectiveMaxDate}
            maxDate={effectiveMaxDate}
            selectsRange
            inline
          />
        </div>
      )}
    </div>
  );
}

export default DateRangePicker;
